import { useTranslation } from "react-i18next";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Button, Checkbox, FormControlLabel, Grid, InputAdornment, Paper, Stack, Typography } from "@mui/material";
import { FormikProps } from "formik";

import { FormikDecimalTextField } from "../../../../../../../../formik/FormikDecimalTextField";
import { FormikTextField } from "../../../../../../../../formik/FormikTextField";
import { FormikYesNoAutocomplete } from "../../../../../../../../formik/FormikYesNoAutocomplete";
import { FormikAutocompleteSelect } from "../../../../../../../../formik/formik-autocomplete/FormikAutocompleteSelect";
import { useClientFileContext } from "../../../../../../../context/ClientFileContext";
import { FieldWithInfo } from "../../../../../personal-data/components/field-with-info/FieldWithInfo";
import {
  AssetIncomeFormValues,
  IncomeEntryFormValue,
  IncomeMaxLength,
  SPECIAL_PAYMENT_COUNT_OPTIONS,
} from "../../../../IncomeView.types";
import { fillMonthsFromDailyRate } from "../../../../IncomeView.utils";
import { useIncomeTypes } from "../../../../hooks/useIncomeTypes";
import { ActionQuestion } from "./components/action-question/ActionQuestion";
import { IncomeAverageFields } from "./components/income-average-fields/IncomeAverageFields";
import { IncomeMonthTable } from "./components/income-month-table/IncomeMonthTable";
import { IncomeTypeSelect } from "./components/income-type-select/IncomeTypeSelect";

const SECTION = "clients.client.debtSettlement.assetInventory.incomeTab.entry";

const FOLLOW_UP_FIELDS: Array<keyof IncomeEntryFormValue> = [
  "isPartiallyGarnished",
  "hasEmployerFamilyAllowance",
  "hasEmployerSettledExpenses",
  "hasBenefitInKind",
  "receivesTips",
  "hasExpenseAllowances",
];

const SPECIAL_PAYMENT_COUNT_LABELS = {
  One: `${SECTION}.specialPaymentCountOptions.one`,
  Two: `${SECTION}.specialPaymentCountOptions.two`,
  ThreeOrMore: `${SECTION}.specialPaymentCountOptions.threeOrMore`,
} as const;

interface Props {
  formik: FormikProps<AssetIncomeFormValues>;
  index: number;
  entry: IncomeEntryFormValue;
  onRemove: (index: number) => void;
}

export function IncomeEntryCard({ formik, index, entry, onRemove }: Props) {
  const { t } = useTranslation();
  const { capabilities } = useClientFileContext();
  const { traitsOf } = useIncomeTypes();
  const traits = traitsOf(entry.incomeType);

  const path = (field: keyof IncomeEntryFormValue) => `incomes.${index}.${field}` as const;
  const fieldId = (field: keyof IncomeEntryFormValue) => `income-${entry.key}-${field}`;

  const withSpecialPayments = entry.hasSpecialPayments === true;
  const showPlausibilityHint = entry.hasSpecialPayments === false && traits.expectsSpecialPayments;
  const title = t(`${SECTION}.title`, { number: index + 1 });

  const specialPaymentCountOptions = SPECIAL_PAYMENT_COUNT_OPTIONS.map((option) => ({
    id: option,
    label: t(SPECIAL_PAYMENT_COUNT_LABELS[option]),
  }));

  const fillFromDailyRate = () => {
    void formik.setFieldValue(path("months"), fillMonthsFromDailyRate(entry));
  };

  const visibleFollowUpFields = FOLLOW_UP_FIELDS.filter(
    (field) => field !== "isPartiallyGarnished" || !traits.isNonSeizable
  );
  const allFollowUpsAnsweredNo = visibleFollowUpFields.every((field) => entry[field] === false);

  const setAllFollowUps = (answeredNo: boolean) => {
    FOLLOW_UP_FIELDS.forEach((field) => void formik.setFieldValue(path(field), answeredNo ? false : null));
  };

  const yesNo = (field: keyof IncomeEntryFormValue, label: string, info?: string) => {
    const input = <FormikYesNoAutocomplete id={fieldId(field)} formik={formik} path={path(field)} label={label} />;

    return info ? (
      <FieldWithInfo fieldLabel={label} info={info}>
        {input}
      </FieldWithInfo>
    ) : (
      input
    );
  };

  const amount = (field: keyof IncomeEntryFormValue, label: string) => (
    <FormikDecimalTextField
      id={fieldId(field)}
      formik={formik}
      path={path(field)}
      label={label}
      decimalScale={2}
      minValue={0}
      slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
    />
  );

  return (
    <Paper variant="outlined" component="article" aria-label={title} sx={{ p: 2 }}>
      <Stack gap={2}>
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>

        <FormikTextField
          formik={formik}
          path={path("payerNameAndAddress")}
          label={t(`${SECTION}.payerNameAndAddress`)}
          slotProps={{ input: { inputProps: { maxLength: IncomeMaxLength.payerNameAndAddress } } }}
        />

        <Grid container columnSpacing={2} rowSpacing={1}>
          <Grid size={{ xs: 12, md: 6 }}>
            <IncomeTypeSelect formik={formik} path={path("incomeType")} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {yesNo("hasSpecialPayments", t(`${SECTION}.hasSpecialPayments`), t(`${SECTION}.hasSpecialPaymentsInfo`))}
            {showPlausibilityHint && (
              <Stack direction="row" alignItems="center" gap={0.5} role="status">
                <ErrorOutlineIcon fontSize="small" color="warning" aria-hidden />
                <Typography variant="caption" color="warning.dark">
                  {t(`${SECTION}.specialPaymentsPlausibility`)}
                </Typography>
              </Stack>
            )}
          </Grid>

          {traits.hasDailyRate && (
            <>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormikDecimalTextField
                  id={fieldId("dailyRate")}
                  formik={formik}
                  path={path("dailyRate")}
                  label={t(`${SECTION}.dailyRate`)}
                  decimalScale={2}
                  minValue={0}
                  slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldWithInfo
                  fieldLabel={t(`${SECTION}.fillFromDailyRate`)}
                  info={t(`${SECTION}.fillFromDailyRateInfo`)}
                >
                  <ActionQuestion
                    label={t(`${SECTION}.fillFromDailyRate`)}
                    onConfirm={fillFromDailyRate}
                    disabled={entry.dailyRate === null}
                  />
                </FieldWithInfo>
              </Grid>
            </>
          )}

          {withSpecialPayments && (
            <Grid size={12}>
              <FormikAutocompleteSelect
                formik={formik}
                path={path("specialPaymentCount")}
                label={t(`${SECTION}.specialPaymentCount`)}
                options={specialPaymentCountOptions}
              />
            </Grid>
          )}
        </Grid>

        <FormikTextField
          formik={formik}
          path={path("note")}
          label={t(`${SECTION}.note`)}
          multiline
          minRows={2}
          slotProps={{ input: { inputProps: { maxLength: IncomeMaxLength.note } } }}
        />

        <Stack gap={1}>
          {capabilities.counselorIncomeHelpers && withSpecialPayments && (
            <FormControlLabel
              sx={{ mx: 0, mt: -1, mb: 2 }}
              control={
                <Checkbox
                  id={`income-${entry.key}-answerFollowUpsWithNo`}
                  checked={allFollowUpsAnsweredNo}
                  onChange={(_, checked) => setAllFollowUps(checked)}
                  sx={{ pl: 0, py: 0 }}
                />
              }
              label={t(`${SECTION}.answerFollowUpsWithNo`)}
            />
          )}
          {!traits.isNonSeizable && yesNo("isPartiallyGarnished", t(`${SECTION}.isPartiallyGarnished`))}
          {withSpecialPayments && (
            <>
              {yesNo("hasEmployerFamilyAllowance", t(`${SECTION}.hasEmployerFamilyAllowance`))}
              {yesNo(
                "hasEmployerSettledExpenses",
                t(`${SECTION}.hasEmployerSettledExpenses`),
                t(`${SECTION}.hasEmployerSettledExpensesInfo`)
              )}
              {yesNo("hasBenefitInKind", t(`${SECTION}.hasBenefitInKind`))}
              {entry.hasBenefitInKind === true && amount("benefitInKindAmount", t(`${SECTION}.benefitInKindAmount`))}
              {yesNo("receivesTips", t(`${SECTION}.receivesTips`))}
              {entry.receivesTips === true && amount("monthlyTipAmount", t(`${SECTION}.monthlyTipAmount`))}
              {yesNo("hasExpenseAllowances", t(`${SECTION}.hasExpenseAllowances`))}
            </>
          )}
        </Stack>

        <IncomeMonthTable formik={formik} index={index} entry={entry} isNonSeizable={traits.isNonSeizable} />
        <IncomeAverageFields formik={formik} index={index} entry={entry} isNonSeizable={traits.isNonSeizable} />
        <Button
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => onRemove(index)}
          sx={{ alignSelf: "flex-end" }}
        >
          {t(`${SECTION}.remove`)}
        </Button>
      </Stack>
    </Paper>
  );
}
